import { IntegrationStatus } from "../../consts/integrations";
import { INTEGRATION_AUTH_NAMES } from "../../../../consts";

const dateFormatter = new Intl.DateTimeFormat(
  window.navigator.language || "en-US",
  {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
);

const humanizeScope = (name) =>
  name.includes(".") ? name : name.charAt(0).toUpperCase() + name.slice(1);

/**
 * Status precedence for a connection row — highest tier wins, at most one
 * strip renders. Pure (no React/redux) so it stays trivially testable.
 * Tier order, copy, icon and colour all taken from
 * assets/handoffs/design_handoff_integrations_page/prototype/integrations-frames.js
 * (the README's tier table is a paraphrase and drifts from this in a few
 * places — the frame markup is the ground truth).
 *
 * @param {object} integration
 * @returns {{
 *   tier: number,
 *   kind: "error"|"warning"|"gray"|"progress"|"healthy",
 *   title: string,
 *   detail?: string,
 *   icon?: string,
 *   chevron?: boolean,
 *   actionType?: "reauth"|"link",
 *   actionLabel?: string,
 *   actionHref?: string,
 * }}
 */
export const getConnectionStatus = (integration) => {
  if (integration.disabled_date) {
    return {
      tier: 1,
      kind: "error",
      icon: "SlashCircle01",
      title: "Disabled when the plan changed",
      detail:
        "This integration is over your plan's limit. Upgrade to switch it back on — its data is still here.",
      actionType: "link",
      actionLabel: "Upgrade",
      actionHref: "/billing",
      disabled: true,
    };
  }

  if (integration.status === IntegrationStatus.reAuthRequired) {
    return {
      tier: 2,
      kind: "error",
      icon: "Lock01",
      title: "Session expired",
      detail: "Revisual can no longer reach this account.",
      actionType: "reauth",
      actionLabel: `Re-authenticate with ${INTEGRATION_AUTH_NAMES[integration.type]}`,
    };
  }

  if (integration.status === IntegrationStatus.permissionsRevoked) {
    const revokedScope = (integration.integration_scopes || []).find(
      (scope) => scope.available && !scope.has_permissions
    );

    return {
      tier: 3,
      kind: "warning",
      icon: "AlertCircle",
      title: `Paused — access to ${
        revokedScope ? humanizeScope(revokedScope.name) : "a required scope"
      } was withdrawn`,
      detail: "Revisual can still sign in, but cannot read data.",
      actionType: "reauth",
      actionLabel: "Update permissions",
    };
  }

  if (integration.status === IntegrationStatus.syncError) {
    return {
      tier: 4,
      kind: "error",
      icon: "AlertCircle",
      title: "Last sync failed",
      detail: integration.last_sync
        ? `Data from ${dateFormatter.format(
            new Date(integration.last_sync * 1000)
          )} onwards may be missing. Auto-sync will try again.`
        : "Auto-sync will try again.",
      chevron: Boolean(integration.last_error_message),
      rawDetail: integration.last_error_message,
    };
  }

  const hasSyncedCalendar = (integration.settings?.calendars || []).some(
    (calendar) => calendar.sync
  );

  if (
    integration.status !== IntegrationStatus.syncInProgress &&
    !hasSyncedCalendar
  ) {
    return {
      tier: 5,
      kind: "error",
      icon: "AlertTriangle",
      title: "Setup incomplete",
      detail: "You left before granting access, so nothing is syncing.",
      actionType: "link",
      actionLabel: "Finish setup",
      actionHref: `/integrations/connect/${integration.type}/calendars?uuid=${integration.uuid}`,
      hideMeta: true,
    };
  }

  if (integration.status === IntegrationStatus.syncInProgress) {
    return {
      tier: 6,
      kind: "progress",
      title: "Syncing",
      hideMeta: true,
    };
  }

  if (integration.status === IntegrationStatus.notSynced) {
    return {
      tier: 7,
      kind: "healthy",
      metaIcon: "Clock",
      metaOk: false,
    };
  }

  return {
    tier: 8,
    kind: "healthy",
    metaIcon: "CheckCircle",
    metaOk: true,
  };
};

export default getConnectionStatus;
